import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Moon, History, Clock, ChevronRight, Loader2, LogIn } from 'lucide-react';
import { useAuth } from '@/components/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MysteriaeHistoryPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [draws, setDraws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDraw, setSelectedDraw] = useState(null);

  useEffect(() => {
    if (isAuthenticated && user?.user_id) {
      fetchDrawHistory();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const fetchDrawHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/mysteriae/draws/${user.user_id}`);
      const data = await response.json();
      setDraws(data.draws || []);
    } catch (error) {
      console.error('Error fetching draw history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-purple-950/30 to-slate-950 flex items-center justify-center px-4">
        <div className="text-center">
          <Moon className="h-16 w-16 text-purple-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Connexion requise</h1>
          <p className="text-slate-400 mb-6">
            Connectez-vous pour accéder à l'historique de vos tirages
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-xl"
          >
            <LogIn className="h-4 w-4 mr-2" />
            Se connecter
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-purple-950/30 to-slate-950">
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-purple-300 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.3 + 0.1,
              animationDelay: `${Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-purple-500/20">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/mysteriae/draw" 
            className="flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
            data-testid="back-to-draw"
          >
            <ChevronLeft className="h-5 w-5" />
            <span className="font-semibold">Tirages</span>
          </Link>
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-purple-400" />
            <span className="font-bold text-white tracking-wide">Historique</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">
              Vos tirages précédents
            </h1>
            <p className="text-slate-400">
              Retrouvez et relisez vos interprétations passées
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 text-purple-400 animate-spin" />
            </div>
          ) : draws.length === 0 ? (
            <div className="text-center py-20">
              <Moon className="h-16 w-16 text-purple-400/50 mx-auto mb-4" />
              <p className="text-slate-400 mb-4">Vous n'avez pas encore de tirages sauvegardés</p>
              <Button
                onClick={() => navigate('/mysteriae/draw')}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-xl"
              >
                Faire un tirage
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {draws.map((draw, index) => (
                <motion.div
                  key={draw.draw_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <button
                    onClick={() => setSelectedDraw(selectedDraw?.draw_id === draw.draw_id ? null : draw)}
                    className={`w-full p-4 rounded-xl border text-left transition-all ${
                      selectedDraw?.draw_id === draw.draw_id
                        ? 'bg-purple-600/20 border-purple-500/60'
                        : 'bg-slate-800/30 border-slate-700/50 hover:border-purple-500/30'
                    }`}
                    data-testid={`draw-${draw.draw_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-semibold">{draw.spread_name}</h3>
                        {draw.question && (
                          <p className="text-purple-400 text-sm mt-1 italic">"{draw.question}"</p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-slate-400 text-xs">
                          <Clock className="h-3 w-3" />
                          <span>{formatDate(draw.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex -space-x-2">
                          {draw.cards?.slice(0, 3).map((card, i) => (
                            <div
                              key={i}
                              className="w-8 h-12 rounded border border-purple-500/30 overflow-hidden"
                            >
                              <img
                                src={card.card?.image_url}
                                alt=""
                                className={`w-full h-full object-cover ${card.is_reversed ? 'rotate-180' : ''}`}
                              />
                            </div>
                          ))}
                        </div>
                        <ChevronRight className={`h-5 w-5 text-purple-400 transition-transform ${
                          selectedDraw?.draw_id === draw.draw_id ? 'rotate-90' : ''
                        }`} />
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {selectedDraw?.draw_id === draw.draw_id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-4 pt-4 border-t border-purple-500/20"
                      >
                        {/* Cards */}
                        <div className="flex flex-wrap gap-3 mb-4">
                          {draw.cards?.map((card, i) => (
                            <div key={i} className="text-center">
                              <div className="w-16 h-24 rounded-lg border border-purple-500/30 overflow-hidden mb-1">
                                <img
                                  src={card.card?.image_url}
                                  alt={card.card?.name}
                                  className={`w-full h-full object-cover ${card.is_reversed ? 'rotate-180' : ''}`}
                                />
                              </div>
                              <p className="text-[10px] text-purple-400">{card.position_name}</p>
                              <p className="text-[10px] text-white">{card.card?.name}</p>
                              {card.is_reversed && (
                                <span className="text-[8px] text-red-400">(R)</span>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Interpretation */}
                        {draw.interpretation && (
                          <div className="p-3 bg-slate-900/50 rounded-lg">
                            <p className="text-slate-300 text-sm whitespace-pre-wrap line-clamp-6">
                              {draw.interpretation}
                            </p>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default MysteriaeHistoryPage;
